import { Injectable, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { CheckoutComponent } from 'src/app/modules/payment/checkout/checkout.component';
import { IProfileDetails } from '../domain/profile.model';
import { IStartUpInfoBase } from '../domain/startup.model';
import { select, Store } from '@ngrx/store';
import { getProfileData } from '../state/profile';
import { getStartUpInfo } from '../state/startup';
import * as ProfileActions from "src/app/core/state/profile/profile.action";
import { IBrandDetails } from '../domain/brand.model';
import { getBrandDetails } from '../state/global';

@Injectable({
  providedIn: 'root'
})
export class ModalHandlerService implements OnDestroy {
  private isModalOpen = false;
  private destroyed$ = new Subject<void>();

  public selectedProgram: any;
  public USER_KEY: string | null = null;
  public PAYMENTDETAILS: any = null;
  public CURRENT_USER: IProfileDetails | null = null;
  public BRAND_DETAIL: IStartUpInfoBase | null = null;
  brandDetails: Partial<IBrandDetails>;
  private readonly BRAND_DETAILS_KEY = 'brandDetails';
  private readonly FALLBACK_USER_KEY = 'user';
  private readonly IP_KEY = 'userPublicIP';
  private readonly MODAL_SETTINGS: NgbModalOptions = {
    windowClass: 'custom-checkout-modal',
    size: 'xl',
    scrollable: true,
    centered: true,
    backdrop: 'static',
    keyboard: false
  };

  constructor(private modalService: NgbModal, private store: Store) {
    this.initStoreSubscriptions();

       this.store.pipe(select(getBrandDetails)).pipe(distinctUntilChanged()).subscribe((res) => {
          if (res) {
            this.brandDetails = res;
          }
        })
  }

  /** Subscribe to profile and brand store */
  private initStoreSubscriptions(): void {
    this.store.pipe(select(getProfileData), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(profile => this.CURRENT_USER = profile || null);

    this.store.pipe(select(getStartUpInfo), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(brand => this.BRAND_DETAIL = brand || null);
  }

  /** Open checkout modal*/
  async openCheckoutModal(options: {
    moduleType: string;
    selectedCycle: string;
    selectedPrice: any;
    userType?: string;
    additionalData?: Record<string, any>;
  }): Promise<any> {
    if (this.isModalOpen) return;

    this.isModalOpen = true;

    try {
      const brandDetails = this.getParsedLocalStorage(this.BRAND_DETAILS_KEY) || this.brandDetails ;
      const userKey = options.moduleType === 'application_programs' ? this.USER_KEY : this.FALLBACK_USER_KEY;
      const user = this.getParsedLocalStorage(userKey);

      const currency = brandDetails?.features?.payment_currency ;
      // Derive the frontend base URL from the API URL. In production the
      // API sits at `https://api.domain.com` and the frontend at
      // `https://domain.com`, so stripping the `api.` subdomain works.
      // In local dev the API is `http://localhost:3000` — use the
      // browser's own origin (e.g. `http://localhost:4200/`) instead.
      let url: string | null = null;
      const apiUrl = brandDetails?.apiUrl || '';
      if (apiUrl.includes('://api.')) {
        url = apiUrl.replace('://api.', '://');
      } else {
        // Local dev or non-standard setup — use the current browser origin
        url = window.location.origin + '/';
      }

      const ref = this.modalService.open(CheckoutComponent, this.MODAL_SETTINGS);
      ref.componentInstance.passData = {
        ...options.additionalData,
        selectedPrice: options.selectedPrice,
        selectedCycle: options.selectedCycle,
        moduleType: options.moduleType,
        activeUser: user,
        userType: options.userType,
        // url:"http://localhost:4200/"
        url: url
      };
      ref.componentInstance.currency =currency ;

      const result = await ref.result;
      return result;
    } catch (error) {
      console.warn('Checkout modal dismissed or failed:', error);
      return null;
    } finally {
      this.isModalOpen = false;
    }
  }

  /** Parse JSON safely from localStorage */
  private getParsedLocalStorage(key: string | null): any {
    if (!key) return null;
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (err) {
      console.error(`Failed to parse localStorage key: ${key}`, err);
      return null;
    }
  }

   getIP(): Promise<string | null> {
    const cachedIp = localStorage.getItem(this.IP_KEY);
    if (cachedIp) {
      return Promise.resolve(cachedIp);
    }

    return fetch('https://checkip.amazonaws.com/')
      .then(res => res.text())
      .then(ip => {
        const cleanIp = ip.trim(); // IMPORTANT
        if (cleanIp) {
          localStorage.setItem(this.IP_KEY, cleanIp);
          return cleanIp;
        }
        return null;
      })
      .catch(err => {
        console.error('IP fetch failed', err);
        return null;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
